import * as css from './SubscriptionRow.css';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {pen, remove, blackDuplicate} from '../../../../assets/symbols/symbols/Symbols.css';
import useShow from '../../../../hooks/useShow';
import List from '../../../../component/list/List';
import {useRef} from 'react';
import useGlobalShow from '../../../../hooks/useGlobalShow';
import {deletePlan, duplicatePlan, setCurrentPlan} from '../../../../redux/subscription/subscriptionActions';
import {SubscriptionPlan} from '../../../../redux/subscription/subscription.type';
import {bounceMessage} from '../../../../layout/userMessages/UserMessage';
import Copy from '../../../../component/copy/Copy';
import {hasPlanSales} from '../../utils/planCalculations';

function SubscriptionOptions({plan}: {plan: SubscriptionPlan | {planTid: number}}) {
    const {t} = useTranslation('subscriptions');
    const {open, close, isOpen, showClass} = useShow();
    const createSubscriptionShow = useGlobalShow('isOpenCreateSubscription');
    const duplicatePlanXHR = useSelector((s: any) => s.subscription.duplicatePlanXHR);
    const planFromStore = useSelector((s: any) =>
        s.subscription.subscriptionPlans?.find((p: SubscriptionPlan) => p.planTid === plan.planTid)
    );
    const {planTid} = plan;
    const refList = useRef(null);
    const planWithSalesData = 'amount' in plan ? plan : planFromStore;

    const onDeletePlan = () => {
        close();
        if(planWithSalesData && hasPlanSales(planWithSalesData)) {
            bounceMessage(t('cannotDeletePlanMessage'), 'warning');
            return;
        }
        bounceMessage(
            t('confirmDeletePlanMessage'),
            'attention',
            {
                text: t('delete'),
                onClick: async () => {
                    const deleted = await deletePlan(planTid);
                    if(deleted && createSubscriptionShow.isOpen) {
                        createSubscriptionShow.close();
                    }
                },
            },
            {text: t('cancel')}
        );
    };

    const onDuplicate = async () => {
        if(duplicatePlanXHR === 'LOADING') return;
        const res = await duplicatePlan(planTid);
        if(res?.plan) {
            const planTitle = 'title' in plan ? plan.title : '';
            bounceMessage(t('duplicateSuccess', {name: planTitle}), 'success');
        }
        close();
    };
    
    const handleEdit = () => {     
        close();
        setCurrentPlan(planTid);
        createSubscriptionShow.open();
    };
    
    const optionsList = [
        {text: t('editSubscription'), onClick: handleEdit, icon: pen, dataCyName: 'edit'},
        {text: t('duplicateSeries'), onClick: onDuplicate, icon: blackDuplicate, dataCyName: 'duplicate'},
        {text: t('delete'), onClick: onDeletePlan, icon: remove, dataCyName: 'remove'},
        {
            text: `${t('seriesItemLabel')}: ${planTid}`,
            rightChildren: <Copy className={undefined} link={String(planTid)} />,
            dataCyName: 'subscriptionPlanTid',
        },
    ];
    return (
        <css.options 
            onClick={(e) => {
                e.stopPropagation();
                isOpen ? close() : open();
            }} 
            data-cy="subscriptionOptions" 
            ref={refList}>
            {isOpen && refList.current && (
                <List 
                    left={refList.current.getBoundingClientRect().x + 'px'} 
                    list={optionsList} 
                    top={40} 
                    showClass={showClass} 
                    close={close}  
                    title={t('additionalActions')} 
                    fixedTop={refList.current.getBoundingClientRect().y}
                />
            )}
        </css.options>
    );
}

export default SubscriptionOptions;

